import type { JSX } from "react";

import { assertNever } from "@vega-core/utils/assertNever";

import {
  type DateRangeFilterConfig,
  type DropdownFilterConfig,
  type FilterConfig,
  type FilterState,
  FilterType,
  type FilterValue,
  type MultiselectFilterConfig,
  type MultiSelectNestedFilterConfig,
  type MultiSelectNestedV2FilterConfig,
  type SearchableDropdownFilterConfig,
  type SearchFilterConfig,
  type SwitchFilterConfig,
  type TableFilterId,
} from "../types";

import { DateRangeFilter } from "./DateRangeFilter";
import { DropdownFilter } from "./DropdownFilter";
import { MultiselectFilter } from "./MultiselectFilter";
import { MultiSelectNestedFilter } from "./MultiselectNestedFilter";
import { MultiSelectNestedV2Filter } from "./MultiSelectNestedV2Filter";
import { SearchableDropdownFilter } from "./SearchableDropdownFilter";
import { SearchFilter } from "./SearchFilter";
import { SwitchFilter } from "./SwitchFilter";

type RenderFilterProps = {
  filter: FilterConfig;
  filterState: FilterState;
  isLoading: boolean;
  onFilterChange: (filterId: string, value: FilterValue) => void;
  showLabel?: boolean;
  tableId: TableFilterId;
  type: FilterType;
  disabled?: boolean;
};

export function RenderFilter({
  filter,
  type,
  filterState,
  isLoading,
  onFilterChange,
  showLabel,
  tableId,
  disabled = false,
}: RenderFilterProps): JSX.Element {
  switch (type) {
    case FilterType.Search:
      return (
        <SearchFilter
          disabled={disabled}
          filter={filter as SearchFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.Dropdown:
      return (
        <DropdownFilter
          disabled={disabled}
          filter={filter as DropdownFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.SearchableDropdown:
      return (
        <SearchableDropdownFilter
          disabled={disabled}
          filter={filter as SearchableDropdownFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.Multiselect:
      return (
        <MultiselectFilter
          disabled={disabled}
          filter={filter as MultiselectFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.MultiSelectNested:
      return (
        <MultiSelectNestedFilter
          disabled={disabled}
          filter={filter as MultiSelectNestedFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.DateRange:
      return (
        <DateRangeFilter
          disabled={disabled}
          filter={filter as DateRangeFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.Switch:
      return (
        <SwitchFilter
          disabled={disabled}
          filter={filter as SwitchFilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    case FilterType.MultiSelectNestedV2:
      return (
        <MultiSelectNestedV2Filter
          disabled={disabled}
          filter={filter as MultiSelectNestedV2FilterConfig}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          showLabel={showLabel}
          tableId={tableId}
        />
      );
    default:
      return assertNever(type);
  }
}
